<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import type { Artwork, BadgeDefinition, PublicationResult } from './api';
  import { submitOrganizationEvent, uploadArtwork } from './api';
  import { identifierFromName, signBadgeDefinition } from './badgeEvents';
  import ArtworkCropper from './ArtworkCropper.svelte';
  import PeacockBadge from './PeacockBadge.svelte';
  import type { SignerConnection } from './signers';
  import { canEmbedApproval } from './signerApproval';

  let { signer, badge = null, bitcoinPlusPlusURL, onclose, onsaved }: {
    signer: SignerConnection;
    bitcoinPlusPlusURL?: string;
    badge?: BadgeDefinition | null;
    onclose: () => void;
    onsaved: (result: PublicationResult) => void;
  } = $props();

  let panel: HTMLElement;
  let name = $state(untrack(() => badge?.name ?? ''));
  let identifier = $state(untrack(() => badge?.identifier ?? ''));
  let description = $state(untrack(() => badge?.description ?? ''));
  let crop = $state<Blob | null>(null);
  let artwork = $state<Artwork | null>(untrack(() => badge ? {
    image: { url: badge.image_url, width: 1024, height: 1024, media_type: 'image/png', sha256: '' },
    thumbnails: badge.thumbnails
  } : null));
  let previewURL = $state(untrack(() => badge?.image_url ?? ''));
  let replacingArtwork = $state(false);
  let manualIdentifier = $state(untrack(() => Boolean(badge)));
  let phase = $state<'edit' | 'upload' | 'sign' | 'publish' | 'done'>('edit');
  let result = $state<PublicationResult | null>(null);
  let error = $state('');
  let approvalURL = $state('');
  let embeddedApproval = $state(false);

  onMount(() => {
    const previous = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    panel.focus();
    return () => {
      document.body.style.overflow = '';
      previous?.focus();
      if (previewURL.startsWith('blob:')) URL.revokeObjectURL(previewURL);
    };
  });

  function artworkChanged(blob: Blob | null) {
    if (previewURL.startsWith('blob:')) URL.revokeObjectURL(previewURL);
    crop = blob;
    artwork = null;
    previewURL = blob ? URL.createObjectURL(blob) : '';
  }

  function nameChanged() {
    if (!manualIdentifier && !badge) identifier = identifierFromName(name);
  }

  function identifierChanged() {
    manualIdentifier = true;
    identifier = identifierFromName(identifier);
  }

  async function publish() {
    error = '';
    if (!name.trim() || !identifier || (!crop && !artwork)) {
      error = 'Add a name, identifier, and artwork before publishing.';
      return;
    }
    try {
      if (crop) {
        phase = 'upload';
        artwork = await uploadArtwork(crop);
        crop = null;
        if (previewURL.startsWith('blob:')) URL.revokeObjectURL(previewURL);
        previewURL = artwork.image.url;
      }
      if (!artwork) throw new Error('Artwork is required.');
      phase = 'sign';
      const event = await signBadgeDefinition(signer, { identifier, name, description, artwork }, (url) => {
        approvalURL = url;
        embeddedApproval = canEmbedApproval(url, bitcoinPlusPlusURL);
        return embeddedApproval;
      });
      phase = 'publish';
      result = await submitOrganizationEvent(event);
      phase = 'done';
      onsaved(result);
    } catch (cause) {
      phase = 'edit';
      error = cause instanceof Error ? cause.message : typeof cause === 'string' && cause.trim() ? cause : 'The badge could not be published.';
    } finally {
      approvalURL = '';
      embeddedApproval = false;
    }
  }

  function keydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && phase === 'edit') {
      onclose();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(panel.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), a[href], iframe')).filter((item) => item.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
</script>

<svelte:window onkeydown={keydown} />

<div class="editor-shell" role="presentation" onclick={(event) => event.currentTarget === event.target && phase === 'edit' && onclose()}>
  <div class="editor" bind:this={panel} role="dialog" aria-modal="true" aria-labelledby="editor-title" tabindex="-1">
    <header>
      <div id="editor-title"><span>BADGE STUDIO / DEFINITION</span><b>{badge ? 'EDIT' : 'NEW'}</b></div>
      <button type="button" aria-label="Close badge editor" onclick={onclose} disabled={phase !== 'edit'}>×</button>
    </header>

    {#if phase === 'done' && result}
      <div class="success">
        <div class="success-art"><PeacockBadge celebrating /></div>
        <p class="eyebrow">signed and preserved</p>
        <h2>Your badge has feathers.</h2>
        <p>{result.published ? 'Relays accepted the definition. It is ready to award.' : 'The definition is safely stored, but no relay accepted it yet. You can retry from the catalog.'}</p>
        <button class="primary" type="button" onclick={onclose}>Return to catalog <span>→</span></button>
      </div>
    {:else if phase === 'sign' && approvalURL}
      <section class="signing-approval">
        <h2>Approve your badge</h2>
        {#if embeddedApproval}<iframe title="Approve badge signing" src={approvalURL}></iframe>{:else}<p>Approve the request in your signer to finish creating this badge.</p>{/if}
        <p><a href={approvalURL} target="_blank" rel="noopener noreferrer" onclick={() => embeddedApproval = false}>Open approval in a new tab ↗</a></p>
      </section>
    {:else}
      <div class="editor-grid">
        <div class="art-column">
          <p class="eyebrow">01 · the emblem</p>
          {#if badge && !replacingArtwork}
            <div class="existing-art">
              <img src={badge.image_url} alt={`Current artwork for ${badge.name}`} />
              <button type="button" onclick={() => { replacingArtwork = true; artwork = null; previewURL = ''; }}>Replace artwork</button>
            </div>
          {:else}
            <ArtworkCropper onchange={artworkChanged} />
          {/if}
          <p class="art-note">Your crop becomes one immutable 1024px image and five responsive thumbnails.</p>
        </div>

        <div class="details-column">
          <p class="eyebrow">02 · the meaning</p>
          <label for="badge-name">Badge name</label>
          <input id="badge-name" bind:value={name} oninput={nameChanged} maxlength="80" placeholder="Community Builder" />

          <label for="badge-id">Permanent identifier</label>
          <div class="identifier"><span>#</span><input id="badge-id" bind:value={identifier} oninput={identifierChanged} maxlength="64" readonly={Boolean(badge)} placeholder="community-builder" /></div>
          <small>This becomes part of the badge’s permanent Nostr address.</small>

          <label for="badge-description">What does this recognize?</label>
          <textarea id="badge-description" bind:value={description} maxlength="1000" rows="6" placeholder="Awarded to people who make the community stronger by…"></textarea>
          <div class="count">{description.length} / 1000</div>

          <div class="mini-preview">
            <div>{#if previewURL}<img src={previewURL} alt="Badge preview" />{:else}<PeacockBadge compact />{/if}</div>
            <span><small>LIVE PREVIEW</small><strong>{name || 'Untitled badge'}</strong><p>{description || 'The meaning behind this badge will appear here.'}</p></span>
          </div>

          {#if error}<p class="error" role="alert">{error}</p>{/if}
          <div class="actions">
            <button class="secondary" type="button" onclick={onclose} disabled={phase !== 'edit'}>Cancel</button>
            <button class="primary" type="button" onclick={publish} disabled={phase !== 'edit'}>
              {phase === 'upload' ? 'Preparing artwork…' : phase === 'sign' ? 'Check your signer…' : phase === 'publish' ? 'Publishing…' : badge ? 'Sign update' : 'Create badge'}
              <span>{phase === 'edit' ? '→' : '···'}</span>
            </button>
          </div>
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .signing-approval { padding: 20px; }
  .signing-approval h2 { margin: 0 0 16px; font-size: 24px; }
  .signing-approval iframe { width: 100%; height: 65dvh; min-height: 300px; border: 0; background: #fffaf3; }
  .signing-approval a { color: var(--violet-dark); font-weight: 700; }

  .editor-shell { position: fixed; z-index: 12; inset: 0; overflow-y: auto; padding: clamp(16px, 4vw, 52px); background: rgba(20, 27, 38, .78); backdrop-filter: blur(12px); }
  .editor { width: min(1080px, 100%); min-height: min(720px, calc(100dvh - 104px)); margin: auto; overflow: hidden; border: 1px solid rgba(255,255,255,.18); background: var(--surface); box-shadow: 0 35px 120px rgba(9,13,24,.45); }
  header { display: flex; align-items: center; justify-content: space-between; min-height: 54px; padding: 8px 15px 8px 22px; border-bottom: 1px solid var(--line); }
  header div { display: flex; gap: 12px; color: var(--muted); font-size: 11px; font-weight: 800; letter-spacing: .12em; }
  header b { color: var(--violet); }
  header button { width: 40px; height: 40px; border: 1px solid var(--line); border-radius: 50%; background: transparent; color: var(--ink); font-size: 25px; cursor: pointer; }
  .editor-grid { display: grid; grid-template-columns: minmax(300px, .82fr) minmax(390px, 1.18fr); }
  .art-column, .details-column { padding: clamp(25px, 4vw, 52px); }
  .art-column { background: var(--surface-soft); border-right: 1px solid var(--line); }
  .eyebrow { margin: 0 0 19px; color: var(--violet); font-size: 10px; font-weight: 850; letter-spacing: .14em; }
  .existing-art { position: relative; padding: 18px; background: var(--teal); }
  .existing-art img { display: block; width: 100%; aspect-ratio: 1; object-fit: cover; }
  .existing-art button { position: absolute; right: 28px; bottom: 28px; min-height: 42px; padding: 8px 14px; border: 0; border-radius: 999px; background: var(--ink); color: white; font-size: 12px; font-weight: 750; cursor: pointer; }
  .art-note { margin: 14px 0 0; color: var(--muted); font-size: 12px; line-height: 1.5; }
  label { display: block; margin: 20px 0 8px; font-size: 11px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
  input, textarea { width: 100%; border: 0; border-bottom: 1px solid var(--line-strong); border-radius: 0; padding: 10px 0; background: transparent; color: var(--ink); font-size: 16px; }
  input:focus, textarea:focus { border-color: var(--violet); }
  textarea { min-height: 118px; resize: vertical; line-height: 1.5; }
  .identifier { display: grid; grid-template-columns: auto 1fr; align-items: center; border-bottom: 1px solid var(--line-strong); }
  .identifier span { color: var(--coral); font-family: var(--display); font-size: 19px; }
  .identifier input { border: 0; font: 13px var(--mono); }
  .details-column > small { display: block; margin-top: 7px; color: var(--muted); font-size: 11px; }
  .count { margin-top: 5px; color: var(--muted); font: 9px var(--mono); text-align: right; }
  .mini-preview { display: grid; grid-template-columns: 76px 1fr; align-items: center; gap: 17px; margin-top: 24px; padding: 13px; border: 1px solid var(--line); background: var(--surface-soft); }
  .mini-preview > div { display: grid; place-items: center; width: 76px; height: 76px; overflow: hidden; border-radius: 50%; background: #173e64; }
  .mini-preview img { width: 100%; height: 100%; object-fit: cover; }
  .mini-preview span { min-width: 0; }
  .mini-preview small { color: var(--teal-dark); font-size: 9px; font-weight: 850; letter-spacing: .12em; }
  .mini-preview strong { display: block; margin-top: 4px; overflow: hidden; font-family: var(--display); font-size: 19px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
  .mini-preview p { margin: 3px 0 0; overflow: hidden; color: var(--muted); font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
  .error { margin: 16px 0 0; padding: 11px 13px; border-left: 3px solid var(--coral-dark); background: #f9e5e5; color: #852f38; font-size: 13px; }
  .actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 24px; }
  .primary, .secondary { min-height: 45px; border-radius: 999px; padding: 10px 17px; font-weight: 780; cursor: pointer; }
  .primary { display: inline-flex; align-items: center; gap: 18px; border: 0; background: var(--ink); color: white; }
  .primary span { color: var(--gold); }
  .secondary { border: 1px solid var(--line-strong); background: transparent; color: var(--ink); }
  .success { display: grid; justify-items: center; max-width: 560px; margin: auto; padding: 65px 30px; text-align: center; }
  .success-art { width: 230px; }
  .success .eyebrow { margin: 7px 0 13px; }
  .success h2 { margin: 0; font-family: var(--display); font-size: clamp(42px, 6vw, 64px); font-weight: 500; letter-spacing: -.04em; }
  .success > p:not(.eyebrow) { max-width: 450px; color: var(--muted); line-height: 1.55; }
  .success .primary { margin-top: 14px; }
  @media (max-width: 760px) { .editor-shell { padding: 0; } .editor { min-height: 100dvh; border: 0; } .editor-grid { grid-template-columns: 1fr; } .art-column { border-right: 0; border-bottom: 1px solid var(--line); } .art-column, .details-column { padding: 28px var(--mobile-gutter); } }
  @media (max-width: 420px) { header { padding-left: 15px; } header div span { display: none; } .actions { display: grid; grid-template-columns: 1fr; } .actions button { width: 100%; justify-content: center; } .actions .primary { grid-row: 1; } .mini-preview { grid-template-columns: 64px 1fr; } .mini-preview > div { width: 64px; height: 64px; } }
</style>
